import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import {
  ShieldCheck, CheckCircle, EnvelopeSimple, ArrowRight, Lock, Info,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = () => ({
  nombre: "", email: "", telefono: "", empresa: "", mensaje: "",
});

/**
 * Two-step demo request dialog:
 *  step 1 = data-processing consent (Ley 21.719 Chile)
 *  step 2 = actual form (nombre / email / teléfono / empresa / mensaje)
 *  step 3 = success message
 */
export default function DemoRequestDialog({ open, onOpenChange }) {
  const [step, setStep] = useState(1);
  const [consent, setConsent] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setStep(1);
    setConsent(false);
    setForm(emptyForm());
    setSubmitting(false);
  };

  const handleOpenChange = (o) => {
    if (!o) reset();
    onOpenChange(o);
  };

  const proceedToForm = () => {
    if (!consent) {
      toast.error("Debes autorizar el tratamiento de tus datos para continuar.");
      return;
    }
    setStep(2);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/demo-request`, { ...form, consent: true });
      setStep(3);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo enviar la solicitud. Intenta más tarde.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg p-0 overflow-hidden" data-testid="demo-request-dialog">
        {step === 1 && (
          <>
            <div className="bg-gradient-to-br from-brand-orange-500 to-brand-orange-600 text-white px-6 py-5">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-white/80">
                <ShieldCheck size={14} weight="fill" /> Autorización previa (Ley 21.719 Chile)
              </div>
              <h2 className="font-display text-2xl font-bold tracking-tight mt-2">
                Antes de continuar
              </h2>
              <p className="mt-1 text-sm text-white/90">
                Nueva Ley de Protección de Datos Personales de Chile.
              </p>
            </div>
            <div className="px-6 pt-5 pb-6 space-y-4">
              <p className="text-sm text-slate-700 leading-relaxed">
                Para procesar tu solicitud de demostración necesitamos que autorices el tratamiento de la
                información que nos entregarás a continuación. Esta autorización es voluntaria, específica,
                informada y puede ser revocada en cualquier momento.
              </p>

              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-2.5 text-sm text-slate-700">
                <div className="font-semibold text-slate-900 text-xs uppercase tracking-wider mb-1">
                  Qué haremos con tus datos
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle size={16} weight="fill" className="text-brand-green-500 flex-shrink-0 mt-0.5" />
                  <span>Usarlos únicamente para contactarte y coordinar la demo solicitada.</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle size={16} weight="fill" className="text-brand-green-500 flex-shrink-0 mt-0.5" />
                  <span>Almacenarlos de forma cifrada durante un máximo de 24 meses.</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle size={16} weight="fill" className="text-brand-green-500 flex-shrink-0 mt-0.5" />
                  <span>No los venderemos, no los cederemos a terceros, ni los utilizaremos con fines publicitarios.</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle size={16} weight="fill" className="text-brand-green-500 flex-shrink-0 mt-0.5" />
                  <span>Podrás solicitar acceso, rectificación o eliminación escribiendo a <strong>contacto@dosoft.cl</strong>.</span>
                </div>
              </div>

              <label
                htmlFor="demo-consent"
                className="flex gap-3 items-start rounded-lg border-2 border-slate-200 hover:border-brand-orange-400 bg-white p-4 cursor-pointer transition"
              >
                <Checkbox
                  id="demo-consent"
                  checked={consent}
                  onCheckedChange={(v) => setConsent(!!v)}
                  className="mt-0.5"
                  data-testid="demo-consent-checkbox"
                />
                <span className="text-sm text-slate-800 leading-relaxed">
                  <strong>Autorizo</strong> a DoSoft SpA a tratar los datos personales que ingresaré para
                  responder mi solicitud de demostración, en los términos descritos y conforme a la{" "}
                  <Link to="/privacidad" target="_blank" className="text-brand-orange-600 hover:underline">
                    Política de Privacidad
                  </Link>{" "}
                  y{" "}
                  <Link to="/terminos" target="_blank" className="text-brand-orange-600 hover:underline">
                    Términos y Condiciones
                  </Link>.
                </span>
              </label>

              <div className="flex items-center gap-2 text-xs text-slate-500 pt-1">
                <Lock size={12} /> Conexión cifrada TLS 1.2+ · Bcrypt · Backups automáticos
              </div>
            </div>
            <DialogFooter className="bg-slate-50 border-t border-slate-200 px-6 py-4">
              <Button
                variant="ghost"
                onClick={() => handleOpenChange(false)}
                data-testid="demo-cancel-btn"
              >
                Cancelar
              </Button>
              <Button
                onClick={proceedToForm}
                disabled={!consent}
                data-testid="demo-proceed-btn"
                className="bg-brand-orange-500 hover:bg-brand-orange-600 text-white gap-2"
              >
                Continuar <ArrowRight size={14} weight="bold" />
              </Button>
            </DialogFooter>
          </>
        )}

        {step === 2 && (
          <>
            <DialogHeader className="px-6 pt-6 pb-2">
              <div className="text-xs uppercase tracking-[0.18em] text-brand-orange-600 mb-1">
                Solicitar demo
              </div>
              <DialogTitle className="font-display text-2xl">Cuéntanos sobre ti</DialogTitle>
              <DialogDescription>
                Completa el formulario y te contactaremos dentro de 24 hrs hábiles.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="px-6 pb-2 space-y-3.5">
              <div>
                <Label htmlFor="d-nombre">Nombre completo *</Label>
                <Input
                  id="d-nombre"
                  data-testid="demo-nombre-input"
                  required
                  minLength={2}
                  value={form.nombre}
                  onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="d-email">Email corporativo *</Label>
                  <Input
                    id="d-email"
                    type="email"
                    required
                    data-testid="demo-email-input"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="d-tel">Teléfono *</Label>
                  <Input
                    id="d-tel"
                    type="tel"
                    required
                    minLength={6}
                    placeholder="+56 9 …"
                    data-testid="demo-telefono-input"
                    value={form.telefono}
                    onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="d-empresa">Empresa u organización *</Label>
                <Input
                  id="d-empresa"
                  required
                  data-testid="demo-empresa-input"
                  value={form.empresa}
                  onChange={(e) => setForm({ ...form, empresa: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="d-msg">Mensaje (opcional)</Label>
                <Textarea
                  id="d-msg"
                  rows={4}
                  placeholder="Cuéntanos qué necesitas resolver o qué te interesa ver…"
                  data-testid="demo-mensaje-input"
                  value={form.mensaje}
                  onChange={(e) => setForm({ ...form, mensaje: e.target.value })}
                  maxLength={2000}
                />
              </div>
              <div className="flex items-start gap-2 text-[11px] text-slate-500 leading-relaxed rounded-md bg-slate-50 border border-slate-200 p-3">
                <Info size={13} className="flex-shrink-0 mt-0.5" />
                <span>
                  Autorización de tratamiento de datos aceptada en el paso anterior. Puedes revocarla en cualquier
                  momento escribiendo a <strong>contacto@dosoft.cl</strong>.
                </span>
              </div>
              <DialogFooter className="border-t border-slate-200 pt-4 mt-2 -mx-6 px-6 pb-4">
                <Button type="button" variant="ghost" onClick={() => setStep(1)} data-testid="demo-back-btn">
                  Volver
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="demo-submit-btn"
                  className="bg-brand-orange-500 hover:bg-brand-orange-600 text-white gap-2"
                >
                  {submitting ? "Enviando…" : (
                    <>
                      Enviar solicitud <ArrowRight size={14} weight="bold" />
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}

        {step === 3 && (
          <div className="px-6 py-10 text-center" data-testid="demo-success">
            <div className="mx-auto w-16 h-16 rounded-full bg-brand-green-50 text-brand-green-600 flex items-center justify-center">
              <EnvelopeSimple size={28} weight="duotone" />
            </div>
            <h2 className="font-display text-2xl font-bold mt-5 tracking-tight">¡Solicitud enviada!</h2>
            <p className="mt-3 text-slate-600 leading-relaxed max-w-md mx-auto">
              Gracias {form.nombre?.split(" ")[0] || ""}. Nuestro equipo comercial se pondrá en contacto contigo dentro
              de las próximas 24 horas hábiles en <strong>{form.email}</strong>.
            </p>
            <Button
              onClick={() => handleOpenChange(false)}
              className="mt-8 bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="demo-close-btn"
            >
              Cerrar
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
